import type { AccessGroupDto } from "@/services/api";
import {
  APP_ACCESS_KEYS,
  APP_ACCESS_LABELS,
  emptyAppAccess,
  mergeAppAccess,
  normalizeRawAppAccess,
  type AppAccessMap,
} from "@/lib/appAccess";
import type { MultiSelectOption } from "@/components/ui/MultiSelect";

export interface UserRow {
  id: string;
  username: string;
  displayName: string;
  role: string;
  groupIds: string[];
  costCenterIds: string[] | null;
  phone: string;
  isTi: boolean;
  locacoesOperador?: boolean;
  isOperadorOnly?: boolean;
  operadorCatalogId?: string | null;
  operadorCatalogNome?: string | null;
  operadorContratosAtivos?: number;
}

interface RawCC {
  id?: number;
  name?: string;
  code?: string;
}

export function extractCcOptions(data: unknown): MultiSelectOption[] {
  const list: RawCC[] = Array.isArray(data)
    ? (data as RawCC[])
    : data != null && typeof data === "object"
      ? (() => {
          const o = data as Record<string, unknown>;
          for (const key of ["data", "results", "content", "items", "costCenters"]) {
            if (Array.isArray(o[key])) return o[key] as RawCC[];
          }
          return [];
        })()
      : [];
  return list
    .map((cc) => {
      const label = cc.code ? `${cc.code} – ${cc.name ?? ""}`.trim() : (cc.name ?? String(cc.id ?? ""));
      return { value: String(cc.id ?? ""), label };
    })
    .filter((o) => o.value.trim() !== "");
}

export function isAdminUser(user: UserRow): boolean {
  return (user.role ?? "").toLowerCase() === "admin";
}

export function isOperadorAccount(user: UserRow): boolean {
  const role = (user.role ?? "").toLowerCase();
  if (role === "operador") return true;
  if (role === "admin") return false;
  return Boolean(user.operadorCatalogId && user.isOperadorOnly);
}

export function countUsersInGroup(groupId: string, users: UserRow[]): number {
  return users.filter((u) => !isAdminUser(u) && (u.groupIds ?? []).includes(groupId)).length;
}

export function groupMemberNames(groupId: string, users: UserRow[]): string[] {
  return users
    .filter((u) => !isAdminUser(u) && (u.groupIds ?? []).includes(groupId))
    .map((u) => u.displayName || u.username);
}

export type EffectiveAccessKind = "admin" | "legacy" | "groups" | "empty";

export interface EffectiveAccess {
  kind: EffectiveAccessKind;
  access: AppAccessMap | null;
  labels: string[];
}

/** Espelha resolveAuthForStoredUser para preview na UI (sem master/siengeFullAccess). */
export function resolveUserEffectiveAccess(user: UserRow, groups: AccessGroupDto[]): EffectiveAccess {
  if (isAdminUser(user)) {
    return { kind: "admin", access: null, labels: [] };
  }

  const gids = user.groupIds ?? [];
  if (gids.length === 0) {
    return { kind: "legacy", access: null, labels: [] };
  }

  const maps = gids
    .map((id) => groups.find((g) => g.id === id))
    .filter((g): g is AccessGroupDto => g != null)
    .map((g) => normalizeRawAppAccess(g.access));

  if (maps.length === 0) {
    return { kind: "empty", access: emptyAppAccess(), labels: [] };
  }

  const access = mergeAppAccess(maps);
  const labels = APP_ACCESS_KEYS.filter((k) => access[k]).map((k) => APP_ACCESS_LABELS[k]);
  return { kind: "groups", access, labels };
}

export function userHasWarnings(user: UserRow): boolean {
  if (isAdminUser(user)) return false;
  const noGroups = (user.groupIds ?? []).length === 0;
  const legacyCc = user.costCenterIds === null;
  const noCc = Array.isArray(user.costCenterIds) && user.costCenterIds.length === 0;
  return noGroups || legacyCc || noCc;
}

/** Vinculado ao cadastro de operadores (com ou sem contrato ativo). */
export function userIsOperadorLinked(user: UserRow): boolean {
  return Boolean(user.operadorCatalogId || user.locacoesOperador);
}

export function enabledModuleKeys(access: AppAccessMap): typeof APP_ACCESS_KEYS[number][] {
  return APP_ACCESS_KEYS.filter((k) => access[k] === true);
}
